// components/chat/EncaminharDialog.js — encaminhar uma mensagem (nov/26).
//
// Escolhe até 5 conversas do MESMO chip (como no WhatsApp; decisão do dono)
// e manda pelo POST /messages/forward. Duas regras da Meta que a tela diz
// antes, porque o atendente não tem como adivinhar:
//
//   - só recebe quem escreveu nas últimas 24h — quem está fora aparece,
//     desabilitado, com o motivo (GET /messages/forward/janelas, lido só
//     quando o diálogo abre);
//   - no WhatsApp do cliente NÃO aparece "Encaminhada" (a API não deixa).
//
// Um destino que falha não impede os outros: a resposta diz quem recebeu e
// por que os outros não, e o diálogo mostra isso antes de fechar.
import { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Dialog, Drawer } from '@mui/material';
import { FiCheck, FiCornerUpRight, FiSearch, FiX, FiAlertTriangle } from 'react-icons/fi';
import api from '../../services/Api';
import { formatarFone } from '../../pages/UserAdmin/crm/theme';
import { resumo } from './citacao';

const SYNE = "'Syne', sans-serif";
const OUTFIT = "'Outfit', sans-serif";
const MAX = 5;

const PALETAS = [1, 2, 3, 4, 5, 6, 7, 8];
function paleta(nome) {
  let h = 0;
  for (const ch of String(nome || '?')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const n = PALETAS[h % PALETAS.length];
  return { bg: `var(--ch-av${n}-bg)`, fg: `var(--ch-av${n}-fg)` };
}

export default function EncaminharDialog({ open, onClose, mensagem, deviceId, conversas, isMobile, onEncaminhado }) {
  const [busca, setBusca] = useState('');
  const [marcados, setMarcados] = useState([]);
  const [abertas, setAbertas] = useState(null);   // Set de ids; null = não deu pra saber
  const [carregando, setCarregando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let vivo = true;
    setBusca(''); setMarcados([]); setResultado(null); setErro(''); setAbertas(null); setCarregando(true);
    api.get('/messages/forward/janelas', { params: { device_id: deviceId } })
      .then((r) => { if (vivo) setAbertas(new Set(r.data?.abertas || [])); })
      .catch(() => { if (vivo) setAbertas(null); })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [open, deviceId]);

  const lista = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const digitos = termo.replace(/\D/g, '');
    const filtradas = (conversas || []).filter((c) => {
      if (!termo) return true;
      return (c.name || '').toLowerCase().includes(termo)
        || (c.whatsapp_name || '').toLowerCase().includes(termo)
        || (digitos && String(c.phone_number || '').includes(digitos));
    });
    // Quem recebe primeiro; dentro de cada grupo, a ordem da lista do chat.
    if (!abertas) return filtradas;
    return [...filtradas.filter((c) => abertas.has(c.id)), ...filtradas.filter((c) => !abertas.has(c.id))];
  }, [conversas, busca, abertas]);

  const alternar = (id) => setMarcados((atual) => (
    atual.includes(id) ? atual.filter((x) => x !== id) : (atual.length >= MAX ? atual : [...atual, id])
  ));

  const enviar = async () => {
    if (!marcados.length || enviando) return;
    setEnviando(true); setErro('');
    try {
      const r = await api.post('/messages/forward', {
        device_id: deviceId, message_id: mensagem.id, client_ids: marcados,
      });
      const dados = r.data || {};
      onEncaminhado?.(dados);
      if (dados.enviados === dados.total) { onClose(); return; }
      setResultado(dados);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErro(typeof d === 'string' ? d : 'Não foi possível encaminhar. Tente de novo.');
    } finally {
      setEnviando(false);
    }
  };

  const nomeDe = (c) => c.name || formatarFone(c.phone_number) || c.phone_number;

  const corpo = (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, fontFamily: OUTFIT, color: 'var(--ch-text)' }}>
      {/* Topo */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: isMobile ? '6px 16px 10px 20px' : '18px 16px 12px 22px', flexShrink: 0 }}>
        <Box component="h2" id="encaminhar-titulo" sx={{ m: 0, flex: 1, fontFamily: SYNE, fontSize: 18, fontWeight: 700 }}>
          Encaminhar mensagem
        </Box>
        <Box component="button" type="button" onClick={onClose} aria-label="Fechar"
          sx={{
            width: 34, height: 34, p: 0, border: 0, borderRadius: '50%', cursor: 'pointer',
            bgcolor: 'var(--ch-inset)', color: 'var(--ch-text2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            '&:hover': { color: 'var(--ch-text)' }, '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2 },
          }}>
          <FiX size={16} strokeWidth={2.4} />
        </Box>
      </Box>

      {/* A mensagem que vai */}
      <Box sx={{ mx: '20px', mb: '12px', p: '10px 12px', borderRadius: '12px', bgcolor: 'var(--ch-surf)', border: '1px solid var(--ch-bdr)', display: 'flex', gap: '10px', flexShrink: 0 }}>
        <Box component="span" aria-hidden sx={{ color: 'var(--ch-cap)', display: 'flex', mt: '2px' }}><FiCornerUpRight size={15} /></Box>
        <Box sx={{ fontSize: 14, lineHeight: 1.45, color: 'var(--ch-text2)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }}>
          {mensagem ? resumo(mensagem, 200) : ''}
        </Box>
      </Box>

      {resultado ? (
        <Box sx={{ flex: 1, overflowY: 'auto', px: '20px', pb: '12px' }}>
          <Box role="status" sx={{ fontSize: 14.5, fontWeight: 600, mb: '10px' }}>
            Encaminhada para {resultado.enviados} de {resultado.total} conversa{resultado.total !== 1 ? 's' : ''}.
          </Box>
          {(resultado.resultados || []).map((r) => (
            <Box key={r.client_id} sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start', py: '8px', borderTop: '1px solid var(--ch-bdr)' }}>
              <Box component="span" aria-hidden sx={{ display: 'flex', mt: '2px', color: r.ok ? 'var(--ch-green-d)' : 'var(--ch-red)' }}>
                {r.ok ? <FiCheck size={16} strokeWidth={3} /> : <FiAlertTriangle size={15} />}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ fontSize: 14, fontWeight: 600 }}>{r.nome || 'Conversa'}</Box>
                {!r.ok && <Box sx={{ fontSize: 13, color: 'var(--ch-red-d)', lineHeight: 1.45 }}>{r.motivo}</Box>}
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <>
          <Box sx={{ px: '20px', flexShrink: 0 }}>
            <Box sx={{
              height: 40, display: 'flex', alignItems: 'center', gap: '8px', px: '12px',
              bgcolor: 'var(--ch-inset)', border: '1px solid transparent', borderRadius: '12px',
              '&:focus-within': { borderColor: 'var(--ch-green-b)', bgcolor: 'var(--ch-card)' },
            }}>
              <FiSearch size={15} color="var(--ch-cap)" aria-hidden />
              <Box component="input" type="search" value={busca} onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar conversa" aria-label="Buscar conversa" autoFocus={!isMobile}
                sx={{
                  flex: 1, minWidth: 0, border: 0, outline: 'none', background: 'none',
                  fontFamily: OUTFIT, fontSize: isMobile ? 16 : 14, color: 'var(--ch-text)',
                  '&::placeholder': { color: 'var(--ch-cap)', opacity: 1 },
                }} />
            </Box>
            <Box sx={{ fontSize: 12.5, color: 'var(--ch-cap)', lineHeight: 1.5, m: '8px 2px 6px' }}>
              Até {MAX} conversas. Só recebe quem escreveu nas últimas 24 horas — é regra da Meta.
            </Box>
          </Box>

          <Box role="group" aria-label="Conversas" sx={{
            flex: 1, minHeight: 0, overflowY: 'auto', px: '12px', pb: '8px',
            '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { background: 'var(--ch-bdr2)', borderRadius: 4 },
          }}>
            {carregando && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={20} sx={{ color: 'var(--ch-green)' }} /></Box>
            )}
            {!carregando && lista.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 3, fontSize: 14, color: 'var(--ch-cap)' }}>Nenhuma conversa com esse nome ou número.</Box>
            )}
            {!carregando && lista.map((c) => {
              const marcado = marcados.includes(c.id);
              const fechada = abertas && !abertas.has(c.id);
              const cheio = !marcado && marcados.length >= MAX;
              const bloqueada = fechada || cheio;
              const pal = paleta(c.name);
              return (
                <Box key={c.id} component="button" type="button" role="checkbox" aria-checked={marcado}
                  aria-disabled={bloqueada || undefined}
                  onClick={() => { if (!bloqueada) alternar(c.id); }}
                  sx={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: '12px', p: '8px 10px',
                    border: 0, borderRadius: '12px', textAlign: 'left', font: 'inherit', color: 'inherit',
                    bgcolor: marcado ? 'var(--ch-sel)' : 'transparent', cursor: bloqueada ? 'default' : 'pointer',
                    opacity: bloqueada ? 0.55 : 1,
                    '&:hover': bloqueada ? {} : { bgcolor: marcado ? 'var(--ch-sel)' : 'var(--ch-hover)' },
                    '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: -2 },
                  }}>
                  <Box component="span" aria-hidden sx={{
                    width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                    border: `1.5px solid ${marcado ? 'var(--ch-green)' : 'var(--ch-bdr2)'}`,
                    bgcolor: marcado ? 'var(--ch-green)' : 'transparent', color: 'var(--ch-sobre-verde)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {marcado && <FiCheck size={12} strokeWidth={3.2} />}
                  </Box>
                  <Box component="span" aria-hidden sx={{
                    width: 38, height: 38, borderRadius: '50%', flexShrink: 0, bgcolor: pal.bg, color: pal.fg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: SYNE, fontSize: 15, fontWeight: 700,
                  }}>
                    {(c.name || '?')[0]?.toUpperCase()}
                  </Box>
                  <Box component="span" sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box component="span" sx={{ fontFamily: SYNE, fontSize: 14.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {nomeDe(c)}
                    </Box>
                    <Box component="span" sx={{ fontSize: 12.5, color: fechada ? 'var(--ch-amber-d)' : 'var(--ch-cap)' }}>
                      {fechada ? 'Fora da janela de 24h' : formatarFone(c.phone_number)}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </>
      )}

      {/* Rodapé */}
      <Box sx={{
        flexShrink: 0, borderTop: '1px solid var(--ch-bdr)',
        p: isMobile ? '12px 16px calc(14px + env(safe-area-inset-bottom))' : '12px 20px 18px',
        display: 'flex', flexDirection: 'column', gap: '10px',
      }}>
        {erro && (
          <Box role="alert" sx={{ p: '8px 12px', borderRadius: '10px', fontSize: 13.5, bgcolor: 'var(--ch-red-bg)', border: '1px solid var(--ch-red-b)', color: 'var(--ch-red-d)' }}>
            {erro}
          </Box>
        )}
        {resultado ? (
          <Box component="button" type="button" onClick={onClose} sx={botao(true)}>Fechar</Box>
        ) : (
          <>
            <Box sx={{ fontSize: 12.5, color: 'var(--ch-cap)', lineHeight: 1.45 }}>
              No WhatsApp do cliente a mensagem chega como nova — sem o aviso “Encaminhada”.
            </Box>
            <Box component="button" type="button" onClick={enviar} disabled={!marcados.length || enviando} sx={botao(marcados.length > 0 && !enviando)}>
              {enviando
                ? <><CircularProgress size={14} sx={{ color: 'inherit' }} /> Encaminhando…</>
                : <><FiCornerUpRight size={16} aria-hidden /> {marcados.length ? `Encaminhar para ${marcados.length}` : 'Escolha as conversas'}</>}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer anchor="bottom" open={open} onClose={onClose}
        slotProps={{ backdrop: { sx: { bgcolor: 'var(--ch-veu)' } } }}
        PaperProps={{
          'aria-labelledby': 'encaminhar-titulo',
          sx: {
            height: '88dvh', bgcolor: 'var(--ch-card)', color: 'var(--ch-text)', backgroundImage: 'none',
            borderRadius: '28px 28px 0 0', boxShadow: '0 -20px 50px -10px rgba(0,0,0,0.5)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          },
        }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: '8px', flexShrink: 0 }}>
          <Box component="span" sx={{ width: 36, height: 5, borderRadius: 999, bgcolor: 'var(--ch-bdr2)' }} />
        </Box>
        {corpo}
      </Drawer>
    );
  }
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      aria-labelledby="encaminhar-titulo"
      slotProps={{ backdrop: { sx: { bgcolor: 'var(--ch-veu)' } } }}
      PaperProps={{
        sx: {
          height: 'min(640px, 86vh)', bgcolor: 'var(--ch-card)', color: 'var(--ch-text)', backgroundImage: 'none',
          border: '1px solid var(--ch-bdr)', borderRadius: '20px', boxShadow: 'var(--ch-sh2)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        },
      }}>
      {corpo}
    </Dialog>
  );
}

function botao(ativo) {
  return {
    height: 46, border: 0, borderRadius: 999, font: 'inherit', fontSize: 15, fontWeight: 600,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
    bgcolor: ativo ? 'var(--ch-green)' : 'var(--ch-inset)', color: ativo ? 'var(--ch-sobre-verde)' : 'var(--ch-cap)',
    cursor: ativo ? 'pointer' : 'default',
    '&:hover': ativo ? { bgcolor: 'var(--ch-green-hover)' } : {},
    '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 3 },
  };
}
